import { useForm } from "react-hook-form";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import React, { useEffect, useState } from "react";
import OptionsPopup, {
  OptionPopupObjectProps,
} from "../../common/OptionsPopup";
import {
  SingleAlertInfo,
  resetDocumentData,
  setSingleAlertObj,
} from "../../../redux/slice/commonSlice";
import { Box, Grid, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import Button from "../../common/Button";
import {
  Cancel,
  DriveFileRenameOutline,
  VisibilityOutlined,
} from "@mui/icons-material";
import Header from "../../common/Header";
import Card from "../../common/Card";
import { fullName } from "../../../utils/Utility";
import SignaturePad, {
  SignatureData,
} from "../../common/signature/SignaturePad";
import { checkAuthentication, useAuthSlice, updateUserInfo } from "../../../redux/slice/authSlice";
import { previewAiServiceAgreementData, resetServiceAgreementStatus, signAiServiceAgreement } from "../../../redux/slice/AI/AiAuditSlice";
import PDFViewer from "../../common/pdf/PDFViewer";
import { useAiAuditReducer } from "../../../redux/slice/AI/AiAuditSlice";
import { title } from "process";

type AiAuditAgreementForm = {
  signature: string;
  title: string;
};

const initialAiAuditAgreement: AiAuditAgreementForm = {
  signature:"",
  title: ""
};

interface AiAuditAgreementProps {
  open: boolean;
  onAgreementSigned: () => void;
  onCancel: () => void;
  userServiceAgreementStatus? : any;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const AiAuditAgreement: React.FC<AiAuditAgreementProps> = ({
  open,
  onAgreementSigned,
  onCancel,
  userServiceAgreementStatus,
}) => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAuthSlice();
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
  } = useForm<AiAuditAgreementForm>({
    defaultValues: initialAiAuditAgreement,
  });
  const [errorIdentifiers, setErrorIdentifiers] = React.useState<string[]>([]);
  const [showOptionPopup, setShowOptionsPopup] =
    React.useState<OptionPopupObjectProps>({ message: "", show: false });
  const [hasPreviewedAgreement, setHasPreviewedAgreement] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentSignature, setCurrentSignature] = useState<SignatureData | null>(null);
  
  // Get PDF content from Redux state
  const { agreementPdfContent, isLoading } = useAiAuditReducer();

  const handleSignatureChange = (signatureData: SignatureData) => {
    // Store signature data in state
    setCurrentSignature(signatureData);
    setErrorIdentifiers((prevValues) => {
      return prevValues.filter((value) => value !== "signature");
    });
  };

  const onPreviewClick = () => {
    if (userServiceAgreementStatus) {
      const signature = getValues("signature");
      const title = getValues("title");
      const data = {
        serviceName: userServiceAgreementStatus.serviceName,
        templateId: userServiceAgreementStatus.templateId,
        sign: currentSignature?.signature || signature,
        signName : currentSignature?.typedText || "" ,
        title: title || ""
      }
      dispatch(previewAiServiceAgreementData(data))
        .then((res) => {
          if(res.meta && res.meta.requestStatus === "fulfilled" && res.payload){
            setHasPreviewedAgreement(true);
            setShowPdfViewer(true);
          }
        })
        .catch((error) => {
          console.error('Error previewing agreement:', error);
          let errorObj: SingleAlertInfo = {
            message: "Failed to preview agreement. Please try again.",
            alertType: "error",
          };
          dispatch(setSingleAlertObj(errorObj));
        });
    }
  };

  const handleSignClick = () => {
    // Check if preview was clicked FIRST
    if (!hasPreviewedAgreement) {
      let previewAgreementAlertObj: SingleAlertInfo = {
        message: `Please "Preview" the agreement before signing`,
        alertType: "error",
      };
      dispatch(setSingleAlertObj(previewAgreementAlertObj));
      return;
    }
    
    // If preview was done, proceed with form submission which will validate signature
    handleSubmit(onSubmit)();
  };

  const onSubmit = async (obj: any) => {
    // At this point, preview has been done and form validation passed
    // Check if signature is provided
    if (!currentSignature?.signature || currentSignature?.signature === "") {
      setErrorIdentifiers((prevValues) => {
        const newValues = [...prevValues];
        if (!newValues.includes("signature")) {
          newValues.push("signature");
        }
        return newValues;
      });
      let signatureAlertObj: SingleAlertInfo = {
        message: "Please provide your signature",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(signatureAlertObj));
      return;
    }
    
    // Clear any signature errors if we get here
    setErrorIdentifiers((prevValues) => {
      return prevValues.filter((value) => value !== "signature");
    });
    
    // All validations passed, show confirmation popup
    setShowOptionsPopup({
      message: "Do you agree to the AI Audit Terms and Conditions?",
      show: true,
      variant: "confirm",
      buttons: [
        {
          name: "Confirm",
          color: "primary",
          onClick: () => signAgreement(obj),
        },
        { name: "Cancel", color: "secondary", onClick: cancelOptionPopup },
      ],
    });
  };

  const signAgreement = async (agreementObj: any) => {
    if (!userServiceAgreementStatus || !currentSignature?.signature) {
      let errorObj: SingleAlertInfo = {
        message: "Missing required information for signing agreement",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(errorObj));
      return;
    }

    try {
      // Call the sign API
      const title = getValues("title");
      const signData = {
        serviceName: userServiceAgreementStatus.serviceName,
        templateId: userServiceAgreementStatus.templateId,
        sign: currentSignature.signature,
        signName: currentSignature.typedText || "",
        title: title || ""
      };

      const result = await dispatch(signAiServiceAgreement(signData));
      
      if (result.meta.requestStatus === "fulfilled") {
        // Close the confirmation popup first
        cancelOptionPopup();
        
        let successObj: SingleAlertInfo = {
          message: `AI Audit Agreement has been signed successfully`,
          alertType: "success",
        };
        dispatch(setSingleAlertObj(successObj));
        
        // CS-1595 - Update subscribedServices in Redux without refreshing entire auth state
        // This prevents losing unsaved form data in parent components
        const currentSubscribedServices = userInfo?.subscribedServices || [];
        const serviceName = userServiceAgreementStatus.serviceName;
        
        // Add the new service if not already present
        if (!currentSubscribedServices.includes(serviceName)) {
          const updatedSubscribedServices = [...currentSubscribedServices, serviceName];
          dispatch(updateUserInfo({ subscribedServices: updatedSubscribedServices }));
        }
        dispatch(resetDocumentData());
        // Call the onAgreementSigned callback to notify parent component
        onAgreementSigned();
        handleCancel();
      } else {
        // Close the confirmation popup on error too
        cancelOptionPopup();
        
        let errorObj: SingleAlertInfo = {
          message: result.payload || "Failed to sign agreement",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(errorObj));
      }
    } catch (error) {
      console.error('Error signing agreement:', error);
      
      // Close the confirmation popup on exception
      cancelOptionPopup();
      
      let errorObj: SingleAlertInfo = {
        message: "An error occurred while signing the agreement",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(errorObj));
    }
  };

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  };

  const handleCancel = () => {
    onCancel();
    dispatch(resetServiceAgreementStatus());
  };

  const signaturePadValidations = {
    name: {
      required: "Name is required.",
    },
    signature: {
      required: "Signature is required.",
    },
    title: {
      required: "Title is required.",
    },
  };

  return (
    <>
      {showPdfViewer && agreementPdfContent && (
        <PDFViewer
          base64String={agreementPdfContent?.content}
          fileName="AI-Medical-Audit-Agreement"
          title="AI Medical Audit Service Agreement"
          isOpen={showPdfViewer}
          onClose={() =>setShowPdfViewer(false)}
        />
      )}
      {showOptionPopup.show && (
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => {setShowOptionsPopup({ message: "", show: false }); dispatch(resetDocumentData());}}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      )}
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleCancel}
        aria-labelledby="ai-audit-dialog-title"
        maxWidth="md"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <DialogTitle
          id="ai-audit-dialog-title"
          sx={{
            pb: 1,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "linear-gradient(to right, rgb(4, 35, 22), #151d23)"
                : "linear-gradient(to left, rgba(255, 255, 255, 1), rgba(204, 204, 204, 1))",
          }}
        >
          <Header
            title="AI Medical Audit Agreement"
            subtitle='Please "Preview" the agreement to view the full terms before signing'
          />
        </DialogTitle>
        <DialogContent>
          <Card>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                {/* <Grid item xs={12} md={6}>
                  <CustomTextField
                    controllerName="userName"
                    control={control}
                    label="User Name"
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    controllerName="userEmail"
                    control={control}
                    label="Email"
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomDatePicker
                    controllerName="signedDate"
                    control={control}
                    label="Date"
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}></Grid> */}
                <Grid item xs={12}>
                  <div style={{ fontSize: "medium" }}>Signature:</div>
                </Grid>
                <Grid item xs={12}>
                  <SignaturePad
                    isError={errorIdentifiers?.includes("signature")}
                    enableTitle={true}
                    onSignatureChange={handleSignatureChange}
                    name="signature"
                    textName="signature"
                    control={control}
                    validationRules={signaturePadValidations}
                    setSignOutside={
                      userInfo &&
                      fullName(
                        null,
                        userInfo.firstName,
                        userInfo.middleName,
                        userInfo.lastName
                      )
                    }
                  />
                </Grid>
              </Grid>
              <Grid container justifyContent="flex-end" spacing={2} mt={2} >
                <Grid item>
                  <Button
                    type="button"
                    color="info"
                    onClick={onPreviewClick}
                    startIcon={<VisibilityOutlined />}
                    fullWidth
                    disabled={isLoading}
                  >
                    Preview
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    type="button"
                    onClick={handleSignClick}
                    startIcon={<DriveFileRenameOutline />}
                    fullWidth
                  >
                    Sign
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    type="button"
                    color="error"
                    onClick={handleCancel}
                    startIcon={<Cancel />}
                    fullWidth
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Card>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AiAuditAgreement;
